import {
  createEmptyCondition,
  createEmptyImGroup,
  ESTIMATION_AP_CONDITION_FIELDS,
  IM_GROUP_LOGIC,
} from './accrualsConfigurationConstants';
import { createEmptyRuleMeta } from './accrualsRulesBuilderUtils';

export { ESTIMATION_AP_CONDITION_FIELDS };

export const ESTIMATION_APPLY_ON = {
  VENDOR: 'vendor',
  PO: 'po',
};

export const ESTIMATION_METHOD_GROUP = {
  AP: 'ap',
  PO: 'po',
};

export const ESTIMATION_METHOD_IDS = {
  AP_01: 'ap-01',
  AP_02: 'ap-02',
  AP_03: 'ap-03',
  PO_01: 'po-01',
  PO_02: 'po-02',
  PO_03: 'po-03',
};

export const ESTIMATION_ALL_VENDORS_ID = '__all_vendors__';

export const ESTIMATION_VENDOR_OPTIONS = [
  { id: '__all_vendors__', name: 'All vendors' },
  { id: 'VND-001', name: 'Acme Corp' },
  { id: 'VND-042', name: 'GlobalTech' },
  { id: 'VND-118', name: 'Pinnacle Supply' },
  { id: 'VND-203', name: 'Summit Industries' },
  { id: 'VND-311', name: 'Meridian Logistics' },
  { id: 'VND-402', name: 'Atlas Materials' },
  { id: 'VND-501', name: 'Keystone Partners' },
  { id: 'VND-610', name: 'Vertex Solutions' },
];

export const ESTIMATION_GL_OPTIONS = [
  '6100 — Accrued Expenses',
  '6200 — Accrued Utilities',
  '2100 — Accounts Payable',
  '2200 — Accrued Liabilities',
];

const SOURCE_COLUMN_OPTIONS = [
  'Invoice amount net',
  'Invoice amount YTD',
  'Invoice amount gross',
];

const LOOKBACK_PERIOD_OPTIONS = [
  'Last 6 months',
  'Last 12 months',
  'Last 24 months',
];

const COMPARABLE_PERIOD_OPTIONS = [
  'Same month last year',
  'Same quarter last year',
  'Prior month',
];

const AGGREGATE_FUNCTION_OPTIONS = [
  'Average (mean)',
  'Median',
  'Sum',
];

const INVOICE_WINDOW_OPTIONS = [
  '15 days',
  '30 days',
  '45 days',
  '60 days',
];

const IR_CONFIRMATION_OPTIONS = [
  'Yes — send IR to confirm period allocation',
  'No — use invoice amount directly',
];

const PO_AMOUNT_FIELD_OPTIONS = [
  'PO amount',
  'Open PO value',
  'Total PO value',
];

const GR_DEDUCTION_OPTIONS = [
  'GR received amount',
  'Goods received amount',
];

const INVOICED_DEDUCTION_OPTIONS = [
  'Invoiced to date',
  'AP posted amount',
];

const SERVICE_DATE_OPTIONS = [
  'PO start date',
  'PO end date',
  'Service start date',
  'Service end date',
];

const CUTOFF_REFERENCE_OPTIONS = [
  'Cycle close date',
  'Period end date',
  'Custom date',
];

const MINIMUM_ELAPSED_OPTIONS = [
  'No minimum',
  '10%',
  '25%',
  '50%',
];

const MISSING_DATES_OPTIONS = [
  'Skip PO — flag for manual review',
  'Use full open balance',
  'Use zero estimate',
];

export const ESTIMATION_METHOD_DEFINITIONS = {
  [ESTIMATION_METHOD_IDS.AP_01]: {
    id: ESTIMATION_METHOD_IDS.AP_01,
    group: ESTIMATION_METHOD_GROUP.AP,
    code: 'AP-01',
    title: 'Last Comparable Invoice',
    description: 'Uses the most recent AP invoice from the same period last year.',
    badge: 'Historical avg',
    configFields: [
      { key: 'sourceColumn', label: 'Source Column', options: SOURCE_COLUMN_OPTIONS, default: 'Invoice amount net' },
      { key: 'lookbackPeriod', label: 'Lookback Period', options: LOOKBACK_PERIOD_OPTIONS, default: 'Last 12 months' },
      { key: 'comparablePeriod', label: 'Comparable Period', options: COMPARABLE_PERIOD_OPTIONS, default: 'Same month last year' },
    ],
    previewLines: (config) => [
      `For each vendor in matched list: Estimate = LAST(${config.sourceColumn || 'Invoice amount net'})`,
      `→ Filter AP postings WHERE Vendor ID IN Matched vendors AND Posting date WITHIN ${(config.lookbackPeriod || 'Last 12 months').toLowerCase()} AND Period = SAME_MONTH_LAST_YEAR(Cutoff date)`,
    ],
  },
  [ESTIMATION_METHOD_IDS.AP_02]: {
    id: ESTIMATION_METHOD_IDS.AP_02,
    group: ESTIMATION_METHOD_GROUP.AP,
    code: 'AP-02',
    title: 'Historical Average',
    description: 'Average AP posting amounts over a lookback window (e.g. last 6 months).',
    badge: 'Historical avg',
    configFields: [
      { key: 'sourceColumn', label: 'Source Column', options: SOURCE_COLUMN_OPTIONS, default: 'Invoice amount YTD' },
      { key: 'lookbackPeriod', label: 'Lookback Period', options: LOOKBACK_PERIOD_OPTIONS, default: 'Last 6 months' },
      { key: 'aggregateFunction', label: 'Aggregate Function', options: AGGREGATE_FUNCTION_OPTIONS, default: 'Average (mean)' },
    ],
    previewLines: (config) => [
      `For each vendor in matched list: Estimate = AVG(${config.sourceColumn || 'Invoice amount YTD'})`,
      `→ Filter AP postings WHERE Vendor ID IN Matched vendors AND Posting date WITHIN ${(config.lookbackPeriod || 'Last 6 months').toLowerCase()}`,
    ],
  },
  [ESTIMATION_METHOD_IDS.AP_03]: {
    id: ESTIMATION_METHOD_IDS.AP_03,
    group: ESTIMATION_METHOD_GROUP.AP,
    code: 'AP-03',
    title: 'Post-Cutoff Invoice',
    description: 'Uses an invoice received shortly after the cutoff date and known to relate to this period.',
    badge: 'Post-cutoff',
    configFields: [
      { key: 'sourceColumn', label: 'Source Column', options: SOURCE_COLUMN_OPTIONS, default: 'Invoice amount net' },
      { key: 'invoiceWindowDays', label: 'Invoice Window (Days after cutoff)', options: INVOICE_WINDOW_OPTIONS, default: '30 days' },
      { key: 'requireIrConfirmation', label: 'Require IR Confirmation', options: IR_CONFIRMATION_OPTIONS, default: 'Yes — send IR to confirm period allocation' },
    ],
    previewLines: (config) => {
      const days = (config.invoiceWindowDays || '30 days').replace(/\D/g, '') || '30';
      return [
        `For each vendor in matched list: Accrual Estimate = ${config.sourceColumn || 'Invoice amount net'}`,
        `→ Find invoices in AP postings WHERE Posting date BETWEEN Cutoff date AND Cutoff date + ${days} days AND Invoice period = current close period`,
      ];
    },
  },
  [ESTIMATION_METHOD_IDS.PO_01]: {
    id: ESTIMATION_METHOD_IDS.PO_01,
    group: ESTIMATION_METHOD_GROUP.PO,
    code: 'PO-01',
    title: 'Full Open Balance',
    description: 'Accrues the complete remaining PO balance.',
    badge: 'Full open',
    configFields: [
      { key: 'poAmountField', label: 'PO Amount Field', options: PO_AMOUNT_FIELD_OPTIONS, default: 'PO amount' },
      { key: 'grDeductionField', label: 'GR Deduction Field', options: GR_DEDUCTION_OPTIONS, default: 'GR received amount' },
      { key: 'invoicedDeductionField', label: 'Invoiced Deduction Field', options: INVOICED_DEDUCTION_OPTIONS, default: 'Invoiced to date' },
    ],
    previewLines: (config) => [
      'For each open PO matching conditions: Accrual Estimate = Open balance',
      `→ Open balance = ${config.poAmountField || 'PO amount'} – ${config.grDeductionField || 'GR received amount'} – ${config.invoicedDeductionField || 'Invoiced to date'}`,
    ],
  },
  [ESTIMATION_METHOD_IDS.PO_02]: {
    id: ESTIMATION_METHOD_IDS.PO_02,
    group: ESTIMATION_METHOD_GROUP.PO,
    code: 'PO-02',
    title: 'GR Less IR',
    description: 'Accrues what has been goods-received but not yet invoiced.',
    badge: 'GR-IR',
    configFields: [
      { key: 'grAmountField', label: 'GR Amount Field (goods confirmed received)', options: GR_DEDUCTION_OPTIONS, default: 'GR received amount' },
      { key: 'apPostedField', label: 'AP Posted Field (already invoiced)', options: INVOICED_DEDUCTION_OPTIONS, default: 'AP posted amount' },
    ],
    previewLines: (config) => [
      `Accrual Estimate = ${config.grAmountField || 'GR received amount'} - ${config.apPostedField || 'AP posted amount'}`,
      '→ If result < 0, set to 0. If result = 0, skip PO.',
    ],
  },
  [ESTIMATION_METHOD_IDS.PO_03]: {
    id: ESTIMATION_METHOD_IDS.PO_03,
    group: ESTIMATION_METHOD_GROUP.PO,
    code: 'PO-03',
    title: 'Pro-rata (Elapsed Time)',
    description: 'Allocates open balance by elapsed service period.',
    badge: 'Pro-rata',
    configFields: [
      { key: 'serviceStartDateField', label: 'Service Start Date Field', options: SERVICE_DATE_OPTIONS, default: 'PO start date' },
      { key: 'serviceEndDateField', label: 'Service End Date Field', options: SERVICE_DATE_OPTIONS, default: 'PO end date' },
      { key: 'cutoffReference', label: 'Cutoff Reference', options: CUTOFF_REFERENCE_OPTIONS, default: 'Cycle close date' },
      { key: 'minimumElapsedRate', label: 'Minimum Elapsed Rate', options: MINIMUM_ELAPSED_OPTIONS, default: 'No minimum' },
      { key: 'ifServiceDatesMissing', label: 'If Service Dates Missing', options: MISSING_DATES_OPTIONS, default: 'Skip PO — flag for manual review' },
    ],
    previewLines: () => [
      'Accrual Estimate = Open balance × Rate',
      '→ Open balance = PO amount – GR received amount – Invoiced to date',
      '→ Rate = Elapsed days / Total service days (capped at 100%)',
    ],
  },
};

export const ESTIMATION_METHODS_BY_APPLY_ON = {
  [ESTIMATION_APPLY_ON.VENDOR]: [
    ESTIMATION_METHOD_IDS.AP_01,
    ESTIMATION_METHOD_IDS.AP_02,
    ESTIMATION_METHOD_IDS.AP_03,
  ],
  [ESTIMATION_APPLY_ON.PO]: [
    ESTIMATION_METHOD_IDS.PO_01,
    ESTIMATION_METHOD_IDS.PO_02,
    ESTIMATION_METHOD_IDS.PO_03,
  ],
};

export const ESTIMATION_RULES_BUILDER_SUBTITLE = 'Match conditions on AP data';
export const ESTIMATION_PO_RULES_BUILDER_SUBTITLE = 'Match conditions on open PO data';
export const ESTIMATION_FILTER_CRITERIA_TITLE = 'Filter Criteria';
export const ESTIMATION_FILTER_CRITERIA_SUBTITLE = 'Define conditions to filter which rows are included';

function defaultConditions(applyOn) {
  if (applyOn === ESTIMATION_APPLY_ON.PO) {
    return [
      { ...createEmptyCondition(0), field: 'supplier_id', operator: 'eq', value: '' },
      { ...createEmptyCondition(1), field: 'open_po_value', operator: 'gt', value: '' },
    ];
  }
  return [
    { ...createEmptyCondition(0), field: 'supplier_id', operator: 'eq', value: '' },
    { ...createEmptyCondition(1), field: 'invoice_amount', operator: 'gt', value: '' },
  ];
}

function defaultGroups(applyOn) {
  const group = createEmptyImGroup(0);
  return [{
    ...group,
    logic: IM_GROUP_LOGIC.AND,
    conditions: defaultConditions(applyOn),
  }];
}

function defaultRuleMeta() {
  return {
    ...createEmptyRuleMeta(2),
    connectors: ['and'],
    useCustomPattern: false,
    customPattern: '1 AND 2',
  };
}

function defaultMethodConfig() {
  return {};
}

export function createEmptyEstimationRule(overrides = {}) {
  const applyOn = overrides.applyOn || ESTIMATION_APPLY_ON.VENDOR;
  const methodIds = ESTIMATION_METHODS_BY_APPLY_ON[applyOn];
  const estimationMethod = overrides.estimationMethod || methodIds[0];
  return {
    id: overrides.id || `rule-${Date.now()}`,
    name: overrides.name || 'New rule',
    badge: overrides.badge || ESTIMATION_METHOD_DEFINITIONS[estimationMethod]?.badge || '',
    active: overrides.active !== undefined ? overrides.active : true,
    applyOn,
    effectiveFrom: overrides.effectiveFrom || '2026-01-01',
    effectiveTo: overrides.effectiveTo || '2026-12-31',
    vendorIds: overrides.vendorIds || [ESTIMATION_ALL_VENDORS_ID],
    groups: overrides.groups || defaultGroups(applyOn),
    groupLogic: overrides.groupLogic || IM_GROUP_LOGIC.AND,
    groupConnectors: Array.isArray(overrides.groupConnectors) ? overrides.groupConnectors : [],
    conditions: overrides.conditions || defaultConditions(applyOn),
    ruleMeta: overrides.ruleMeta || defaultRuleMeta(),
    estimationMethod,
    methodConfig: overrides.methodConfig || defaultMethodConfig(),
    defaultGlEnabled: overrides.defaultGlEnabled !== undefined ? overrides.defaultGlEnabled : false,
    debitGl: overrides.debitGl || '',
    creditGl: overrides.creditGl || '',
  };
}

export const MOCK_ESTIMATION_RULES = [
  createEmptyEstimationRule({
    id: 'rule-1',
    name: 'AWS — Historical average',
    badge: 'Historical avg',
    estimationMethod: ESTIMATION_METHOD_IDS.AP_02,
    methodConfig: defaultMethodConfig(),
    vendorIds: ['VND-001'],
  }),
  createEmptyEstimationRule({
    id: 'rule-2',
    name: 'Service POs — pro-rata',
    badge: 'Pro-rata',
    applyOn: ESTIMATION_APPLY_ON.VENDOR,
    estimationMethod: ESTIMATION_METHOD_IDS.AP_01,
    methodConfig: defaultMethodConfig(),
    vendorIds: ['VND-001', 'VND-042', 'VND-118'],
    defaultGlEnabled: true,
  }),
  createEmptyEstimationRule({
    id: 'rule-3',
    name: 'Goods receipt — GR less',
    badge: 'GR-IR',
    applyOn: ESTIMATION_APPLY_ON.PO,
    estimationMethod: ESTIMATION_METHOD_IDS.PO_02,
    methodConfig: defaultMethodConfig(),
  }),
  createEmptyEstimationRule({
    id: 'rule-4',
    name: 'Catch-all — Full open amount',
    badge: 'Full open',
    applyOn: ESTIMATION_APPLY_ON.PO,
    estimationMethod: ESTIMATION_METHOD_IDS.PO_01,
    methodConfig: defaultMethodConfig(),
  }),
  createEmptyEstimationRule({
    id: 'rule-5',
    name: 'Post-cutoff invoice — utilities',
    badge: 'Post-cutoff',
    applyOn: ESTIMATION_APPLY_ON.PO,
    estimationMethod: ESTIMATION_METHOD_IDS.AP_03,
    methodConfig: defaultMethodConfig(),
  }),
  createEmptyEstimationRule({
    id: 'rule-6',
    name: 'Service Invoices — accrued',
    badge: 'Accrued',
    applyOn: ESTIMATION_APPLY_ON.VENDOR,
    estimationMethod: ESTIMATION_METHOD_IDS.AP_02,
    methodConfig: defaultMethodConfig(),
  }),
  createEmptyEstimationRule({
    id: 'rule-7',
    name: 'Freight — full open',
    badge: 'Full open',
    applyOn: ESTIMATION_APPLY_ON.PO,
    estimationMethod: ESTIMATION_METHOD_IDS.PO_01,
    methodConfig: defaultMethodConfig(),
  }),
  createEmptyEstimationRule({
    id: 'rule-8',
    name: 'Inventory Write-offs — estimated',
    badge: 'Estimated write-off',
    applyOn: ESTIMATION_APPLY_ON.VENDOR,
    estimationMethod: ESTIMATION_METHOD_IDS.AP_01,
    methodConfig: defaultMethodConfig(),
  }),
  createEmptyEstimationRule({
    id: 'rule-9',
    name: 'Tax Liabilities — outstanding',
    badge: 'Outstanding tax',
    applyOn: ESTIMATION_APPLY_ON.VENDOR,
    estimationMethod: ESTIMATION_METHOD_IDS.AP_02,
    methodConfig: defaultMethodConfig(),
  }),
];

export function getEstimationMethodsForApplyOn(applyOn) {
  const ids = ESTIMATION_METHODS_BY_APPLY_ON[applyOn] || [];
  return ids.map((id) => ESTIMATION_METHOD_DEFINITIONS[id]).filter(Boolean);
}

export function sourceForApplyOn(applyOn) {
  return applyOn === ESTIMATION_APPLY_ON.PO ? 'open_po' : 'ap_postings';
}

export function methodIdFromTemplateCode(code) {
  const upper = String(code || '').toUpperCase();
  const match = Object.values(ESTIMATION_METHOD_DEFINITIONS).find((def) => def.code === upper);
  return match?.id || String(code || '').toLowerCase();
}

export function catalogTemplatesToMethods(templates, applyOn) {
  const source = sourceForApplyOn(applyOn);
  return (templates || [])
    .filter((tmpl) => tmpl && tmpl.enabled !== false && tmpl.source === source)
    .map((tmpl) => {
      const id = methodIdFromTemplateCode(tmpl.code);
      const fallback = ESTIMATION_METHOD_DEFINITIONS[id] || {};
      return {
        id,
        code: tmpl.code || fallback.code || '',
        title: tmpl.title || fallback.title || tmpl.code || '',
        description: tmpl.description || fallback.description || '',
        badge: fallback.badge || tmpl.code || '',
        previewLines: Array.isArray(tmpl.preview_lines) ? tmpl.preview_lines : null,
      };
    })
    .filter((method) => method.id && method.code);
}

export function getMethodPreviewLines(methodId, methodConfig = {}, catalogMethod = null) {
  if (Array.isArray(catalogMethod?.previewLines) && catalogMethod.previewLines.length) {
    return catalogMethod.previewLines;
  }
  const definition = ESTIMATION_METHOD_DEFINITIONS[methodId];
  if (!definition?.previewLines) return [];
  return definition.previewLines(methodConfig);
}

export function formatVendorLabel(vendor) {
  if (!vendor) return '';
  if (vendor.id === ESTIMATION_ALL_VENDORS_ID) return vendor.name;
  return `${vendor.name} (${vendor.id})`;
}
